// src/plugins/sales-forecast/components/LightDarkModeSwitch.tsx
//
// A self-contained hook + component.
//
// INTEGRATION (4 steps):
// ─────────────────────────────────────────────────────────────────────────────
// 1. In SaveForecastButton.tsx, add these imports:
//      import { useLightDarkMode, LightDarkModeSwitch } from "./LightDarkModeSwitch";
//
// 2. Inside the SaveForecastButton component body, add:
//      const { theme: manualTheme, toggleTheme } = useLightDarkMode();
//
// 3. In SaveForecastButton's JSX return, place the toggle just before the
//    Refresh Data button (they share the same flex row):
//      <LightDarkModeSwitch theme={manualTheme} onToggle={toggleTheme} />
//      {/* existing Refresh Data button */}
//      {/* existing Save Forecast button */}
//
// 4. In index.tsx, replace:
//      import { useSystemTheme } from "./hooks/useSystemTheme";
//      const theme = useSystemTheme();
//    with:
//      import { useLightDarkMode } from "./components/LightDarkModeSwitch";
//      const { theme } = useLightDarkMode();
//    The `className={`plugin-root theme-${theme}`}` line stays unchanged.
//
// 5. Append the two CSS class blocks (shown at the bottom of this file as
//    comments) to styles/theme.css.
// ─────────────────────────────────────────────────────────────────────────────
//
// ADD TO BOTTOM OF styles/theme.css:
// ─────────────────────────────────────────────────────────────────────────────
// /* Manual dark override — wins over @media (prefers-color-scheme: dark) */
// .plugin-root.theme-dark {
//   --background-color: #1b232a;
//   --text-color: #e8edee;
//   --muted-text-color: #a9b4b9;
//   --border-color: #2e3a41;
//   --card-bg: #212a32;
//   --subcard-bg: #2a353e;
//   --shadow-color: rgba(0, 0, 0, 0.7);
//   --accent-color: #6ec8c8;
//   --accent-hover: #9ed9d9;
//   --button-text-color: #ffffff;
//   --error-bg: #392020;
//   --error-text: #ff9090;
//   --chart-fill-spec: #5ba4b4;
//   --chart-fill-presold: #9ed9d9;
//   --chart-fill-unsold: #b58ed9;
//   --chart-fill-available: #445c67;
//   --chart-fill-line: #b8c5c9;
// }
// /* Manual light override — forces light even when OS is in dark mode */
// .plugin-root.theme-light {
//   --background-color: #f7f9fa;
//   --text-color: #1a1f23;
//   --muted-text-color: #5a6771;
//   --border-color: #d6dde2;
//   --card-bg: #ffffff;
//   --subcard-bg: #f1f3f5;
//   --shadow-color: rgba(0, 0, 0, 0.05);
//   --accent-color: #3a7ca5;
//   --accent-hover: #31678a;
//   --button-text-color: #ffffff;
//   --error-bg: #ffe6e6;
//   --error-text: #cc0000;
//   --chart-fill-spec: #3a7ca5;
//   --chart-fill-presold: #77c5c5;
//   --chart-fill-unsold: #b8d7e0;
//   --chart-fill-available: #e0e5e8;
//   --chart-fill-line: #4a5a64;
// }
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
export type Theme = "light" | "dark";
const STORAGE_KEY = "sf-theme-override";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getSystemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

function writeStored(t: Theme | null): void {
  try {
    t === null
      ? localStorage.removeItem(STORAGE_KEY)
      : localStorage.setItem(STORAGE_KEY, t);
  } catch {}
}

// ─── Hook (drop-in replacement for useSystemTheme) ────────────────────────────
/**
 * Returns the resolved theme and a stable toggle callback.
 * Persists the user's manual choice to localStorage; falls back to OS pref.
 */
export function useLightDarkMode(): { theme: Theme; toggleTheme: () => void } {
  const [systemTheme, setSystemTheme] = useState<Theme>(getSystemTheme);
  const [override, setOverride] = useState<Theme | null>(readStored);

  // Stay in sync with OS changes when no manual override is set
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const theme: Theme = override ?? systemTheme;

  const toggleTheme = useCallback(() => {
    setOverride((prev) => {
      const next: Theme = (prev ?? getSystemTheme()) === "dark" ? "light" : "dark";
      writeStored(next);
      return next;
    });
  }, []);

  return { theme, toggleTheme };
}

// ─── Component ────────────────────────────────────────────────────────────────
interface LightDarkModeSwitchProps {
  theme: Theme;
  onToggle: () => void;
}

/**
 * Sun ☀ — [track] — Moon ☽  toggle.
 *
 * Sits inline with the "Refresh Data" / "Save Forecast" buttons.
 * Matches their border-radius, padding, and border exactly.
 * Uses only CSS variables from theme.css — zero extra stylesheet needed.
 */
export function LightDarkModeSwitch({ theme, onToggle }: LightDarkModeSwitchProps) {
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        // Mirrors the Refresh Data button geometry from SaveForecastButton
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.35rem",
        padding: "0.45rem 0.65rem",
        borderRadius: "10px",
        border: "1px solid var(--border-color)",
        backgroundColor: "transparent",
        color: "var(--text-color)",
        fontSize: "0.9rem",
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        transition: "background-color 0.2s ease, color 0.2s ease",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      {/* ☀ Sun — full opacity in light mode, dimmed in dark mode */}
      <SunIcon
        style={{
          opacity: isDark ? 0.35 : 1,
          transition: "opacity 0.2s ease",
        }}
      />

      {/* Sliding pill track */}
      <span
        aria-hidden="true"
        style={{
          position: "relative",
          display: "inline-block",
          width: "2rem",
          height: "1.1rem",
          borderRadius: "999px",
          backgroundColor: isDark ? "var(--accent-color)" : "var(--border-color)",
          flexShrink: 0,
          transition: "background-color 0.25s ease",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            width: "0.8rem",
            height: "0.8rem",
            borderRadius: "50%",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            transform: `translateY(-50%) translateX(${isDark ? "1.1rem" : "0.15rem"})`,
            transition: "transform 0.25s ease",
          }}
        />
      </span>

      {/* ☽ Moon — full opacity in dark mode, dimmed in light mode */}
      <MoonIcon
        style={{
          opacity: isDark ? 1 : 0.35,
          transition: "opacity 0.2s ease",
        }}
      />
    </button>
  );
}

// ─── SVG sub-components ───────────────────────────────────────────────────────

function SunIcon({ style }: { style?: React.CSSProperties }) {
  return (
    <svg
      aria-hidden="true"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2"  x2="12" y2="5"  />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="4.22"  y1="4.22"  x2="6.34"  y2="6.34"  />
      <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
      <line x1="2"  y1="12" x2="5"  y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <line x1="4.22"  y1="19.78" x2="6.34"  y2="17.66" />
      <line x1="17.66" y1="6.34"  x2="19.78" y2="4.22"  />
    </svg>
  );
}

function MoonIcon({ style }: { style?: React.CSSProperties }) {
  return (
    <svg
      aria-hidden="true"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}